import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import { formatMoney } from '../utils/money';
import { formatDateTime } from '../utils/format';
import './Approvals.css';

// Line-item requests carry their commercial context + a link to the parent
// Opportunity (group) page where the approver reviews the full picture.
function RequestContext({ request }) {
  if (request.entity_type !== 'line_item' || !request.group_id) return null;
  return (
    <div className="approval-context">
      <Link to={`/pipeline/groups/${request.group_id}`}>
        {request.group_ref} — {request.group_title}
      </Link>
      {' · '}{request.opp_ref} ({request.product ? request.product.replace(/_/g, ' ') : request.line_description})
      {' · '}{formatMoney(request.value_minor_units, request.currency)}
    </div>
  );
}

function StatusChip({ status }) {
  return <span className={`approval-chip approval-${status}`}>{status}</span>;
}

// R1 (DL-25): a contract variation proposes a replacement schedule for a WON
// (pricing-locked) line — the approver sees exactly what changes, old → new.
function VariationDelta({ request }) {
  if (request.request_type !== 'contract_variation') return null;
  const proposed = request.payload?.schedule || [];
  const current = Array.isArray(request.current_pricing_schedule) ? request.current_pricing_schedule : [];
  const currency = request.currency || 'GBP';
  const byKey = (list) => new Map(list.map(entry => [entry.key, entry]));
  const currentMap = byKey(current);
  const proposedMap = byKey(proposed);
  const lines = [];
  for (const [key, entry] of proposedMap) {
    const prev = currentMap.get(key);
    const tag = entry.recurring ? ' (monthly)' : '';
    if (!prev) {
      lines.push(`Added: ${entry.label}${tag} ${formatMoney(entry.amount_minor_units, currency)}`);
    } else if (prev.amount_minor_units !== entry.amount_minor_units || Boolean(prev.recurring) !== Boolean(entry.recurring)) {
      lines.push(`${entry.label}${tag}: ${formatMoney(prev.amount_minor_units, currency)} → ${formatMoney(entry.amount_minor_units, currency)}`);
    }
  }
  for (const [key, entry] of currentMap) {
    if (!proposedMap.has(key)) {
      lines.push(`Removed: ${entry.label}${entry.recurring ? ' (monthly)' : ''} ${formatMoney(entry.amount_minor_units, currency)}`);
    }
  }
  if (lines.length === 0) return null;
  return (
    <div className="approval-reason variation-delta">
      <span>Proposed changes:</span>
      <ul>
        {lines.map((line, index) => <li key={index}>{line}</li>)}
      </ul>
    </div>
  );
}

function Approvals() {
  const { authFetch } = useAuth();
  const { toastElement, showToast } = useToast();
  const [pending, setPending] = useState([]);
  const [mine, setMine] = useState([]);
  // Engineers asking for a completed work log to be reopened (Gareth
  // 2026-10-13). Listed here because managers and admins have no Today page;
  // reopened or declined on the ticket's or job's Work Logs tab.
  const [reopenRequests, setReopenRequests] = useState([]);
  const [notes, setNotes] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, m, r] = await Promise.all([
        authFetch('/api/v1/approvals/pending'),
        authFetch('/api/v1/approvals/mine'),
        // Never holds up the approvals themselves.
        authFetch('/api/v1/work-logs/reopen-requests').catch(() => null)
      ]);
      setPending(p.ok ? (await p.json()).data || [] : []);
      setMine(m.ok ? (await m.json()).data || [] : []);
      const waiting = r && r.ok ? (await r.json().catch(() => ({}))).data : null;
      setReopenRequests(Array.isArray(waiting) ? waiting : []);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const decide = async (request, decision) => {
    const note = (notes[request.request_id] || '').trim();
    if (decision === 'rejected' && !note) {
      showToast('A rejection reason is required', 'error');
      return;
    }
    const res = await authFetch(`/api/v1/approvals/${request.request_id}/decision`, {
      method: 'PUT',
      body: JSON.stringify({ decision, note: note || null })
    });
    if (res.ok) {
      showToast(`Request ${decision}`, 'success');
      setNotes(prev => ({ ...prev, [request.request_id]: '' }));
      load();
    } else {
      const e = await res.json();
      showToast(e.message || 'Failed to record decision', 'error');
    }
  };

  const label = (r) => {
    if (r.request_type === 'contract_variation') return `Contract variation${r.opp_ref ? ` — ${r.opp_ref}` : ''}`;
    return `${(r.request_type || '').replace(/_/g, ' ')}${r.entity_type ? ` · ${r.entity_type}` : ''}`;
  };

  if (loading) return <div className="page-loading">Loading approvals…</div>;

  return (
    <div className="approvals-page">
      {toastElement}
      <h1>Approvals</h1>

      <section className="approvals-section">
        <h2>Awaiting your approval</h2>
        {pending.length === 0 && reopenRequests.length === 0 && <p className="approvals-empty">Nothing awaiting your approval.</p>}
        {reopenRequests.map(r => (
          <div key={r.reopen_id} className="approval-card">
            <div className="approval-card-head">
              <div>
                <div className="approval-title">Work log reopen — {r.ref}{r.title ? ` · ${r.title}` : ''}</div>
                <div className="approval-meta">Requested by <strong>{r.requested_by_name || 'an engineer'}</strong>
                  {r.requested_at && <> · {formatDateTime(r.requested_at)}</>}
                  {r.customer_name && <> · {r.customer_name}</>}</div>
              </div>
              <StatusChip status="pending" />
            </div>
            {r.request_reason && <div className="approval-reason"><span>Reason:</span> {r.request_reason}</div>}
            <div className="approval-actions">
              <Link
                className="btn-primary btn-small"
                to={r.task_id ? `/tasks/${r.task_id}?tab=work` : `/tickets/${r.ticket_id}?tab=work`}
              >
                Open the work log to reopen or decline
              </Link>
            </div>
          </div>
        ))}
        {pending.map(r => (
          <div key={r.request_id} className="approval-card">
            <div className="approval-card-head">
              <div>
                <div className="approval-title">{label(r)}</div>
                <div className="approval-meta">Requested by <strong>{r.requested_by_name}</strong>
                  {r.approver_group_name && <> · via {r.approver_group_name}</>}</div>
              </div>
              <StatusChip status={r.status} />
            </div>
            <RequestContext request={r} />
            {r.reason && <div className="approval-reason"><span>Reason:</span> {r.reason}</div>}
            <VariationDelta request={r} />
            <div className="approval-actions">
              <input
                className="approval-note"
                placeholder="Note (required to reject)…"
                value={notes[r.request_id] || ''}
                onChange={(e) => setNotes(prev => ({ ...prev, [r.request_id]: e.target.value }))}
              />
              <button className="btn-primary btn-small" onClick={() => decide(r, 'approved')}>Approve</button>
              <button className="btn-danger btn-small" onClick={() => decide(r, 'rejected')}>Reject</button>
            </div>
          </div>
        ))}
      </section>

      <section className="approvals-section">
        <h2>My requests</h2>
        {mine.length === 0 && <p className="approvals-empty">You haven't raised any approval requests.</p>}
        {mine.map(r => (
          <div key={r.request_id} className="approval-card">
            <div className="approval-card-head">
              <div>
                <div className="approval-title">{label(r)}</div>
                {r.decided_by_name && <div className="approval-meta">Decided by {r.decided_by_name}</div>}
              </div>
              <StatusChip status={r.status} />
            </div>
            <RequestContext request={r} />
            {r.reason && <div className="approval-reason"><span>Reason:</span> {r.reason}</div>}
            {r.decision_note && <div className="approval-reason"><span>Note:</span> {r.decision_note}</div>}
          </div>
        ))}
      </section>
    </div>
  );
}

export default Approvals;
